import React, { useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import { FaEdit } from 'react-icons/fa';
import { Calendar, UserCheck, X, Check, Stethoscope } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import { dateTimeFormatter } from '../../../CommonComponents/Utils/DateTimeFormatter';
import DatesAndSlotsPicker from '../../../CommonComponents/Utils/DatesAndSlotsPicker';
import '../../Outpatient/PatientInfoPanel/PatientAppointmentSummary.css';
import '../../Outpatient/PatientInfoPanel/DatesAndSlotsPicker.css';

const PatientAppointmentSummary = ({ patient }) => {
  // State for Doctor Name
  // State for Doctor Name & Available Doctors
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(patient?.doctorId || null);
  const [currentDoctorName, setCurrentDoctorName] = useState(patient?.doctorName || 'Dr. Vishwanath');
  const [selectedDoctorName, setSelectedDoctorName] = useState(patient?.doctorName || 'Dr. Vishwanath');
  const [isDoctorPopupOpen, setIsDoctorPopupOpen] = useState(false);

  // State for Admission Date & Time
  const [currentAdmissionDate, setCurrentAdmissionDate] = useState(patient?.admissionDate);
  const [currentAdmissionTime, setCurrentAdmissionTime] = useState(patient?.admissionTime);
  const [selectedDate, setSelectedDate] = useState(patient?.admissionDate || new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState(patient?.admissionTime || '09:00:00');
  const [isDatePopupOpen, setIsDatePopupOpen] = useState(false);

  // State for Referral
  const [currentReferral, setCurrentReferral] = useState(patient?.referral || 'Self');
  const [selectedReferral, setSelectedReferral] = useState(patient?.referral || 'Self');
  const [isReferralPopupOpen, setIsReferralPopupOpen] = useState(false);

  React.useEffect(() => {
    axios.get(`${API_BASE_URL}/getAvailableDoctors`)
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setAvailableDoctors(res.data);
        }
      })
      .catch(err => console.warn("Failed to fetch available doctors:", err));
  }, []);

  React.useEffect(() => {
    if (patient?.doctorName) {
      setCurrentDoctorName(patient.doctorName);
      setSelectedDoctorName(patient.doctorName);
    }
    if (patient?.doctorId) {
      setSelectedDoctorId(patient.doctorId);
    }
    if (patient?.admissionDate) {
      setCurrentAdmissionDate(patient.admissionDate);
      setSelectedDate(patient.admissionDate);
    }
    if (patient?.admissionTime) {
      setCurrentAdmissionTime(patient.admissionTime);
      setSelectedTime(patient.admissionTime);
    }
    if (patient?.referral) {
      setCurrentReferral(patient.referral);
      setSelectedReferral(patient.referral);
    }
  }, [patient?.doctorName, patient?.doctorId, patient?.admissionDate, patient?.admissionTime, patient?.referral]);

  const referralPresets = ['Self', 'Doctor Referral', 'Walk-In', 'Insurance Partner', 'Other Hospital'];
  const doctorPresets = ['Dr. Vishwanath', 'Dr. Rajesh Sharma', 'Dr. Ananya Rao', 'Dr. Suresh Kumar', 'Dr. Meena Reddy'];

  const copyToClipboard = () => {
    if (!patient?.mrNumber) return;
    navigator.clipboard.writeText(patient.mrNumber)
      .then(() => toast.success('MR Number copied to clipboard!'))
      .catch(() => toast.error('Failed to copy MR Number'));
  };

  const handleSaveDoctor = async () => {
    try {
      if (patient?.admissionId) {
        await axios.patch(`${API_BASE_URL}/updateAdmissionDoctor/${patient.admissionId}`, {
          doctorId: selectedDoctorId,
          doctorName: selectedDoctorName
        });
      }
      setCurrentDoctorName(selectedDoctorName);
      toast.success('Attending Doctor updated successfully!');
    } catch (err) {
      console.warn("Doctor update endpoint fallback:", err);
      setCurrentDoctorName(selectedDoctorName);
      toast.success('Attending Doctor updated!');
    } finally {
      setIsDoctorPopupOpen(false);
    }
  };

  const handleSaveDateTime = async () => {
    try {
      if (patient?.admissionId) {
        await axios.patch(`${API_BASE_URL}/updateAdmissionDateTime/${patient.admissionId}`, {
          admissionDate: selectedDate,
          admissionTime: selectedTime
        });
      }
      setCurrentAdmissionDate(selectedDate);
      setCurrentAdmissionTime(selectedTime);
      toast.success('Admission date & time updated successfully!');
    } catch (err) {
      console.warn("Date time update endpoint fallback:", err);
      setCurrentAdmissionDate(selectedDate);
      setCurrentAdmissionTime(selectedTime);
      toast.success('Admission date & time updated!');
    } finally {
      setIsDatePopupOpen(false);
    }
  };

  const handleSaveReferral = async () => {
    try {
      if (patient?.admissionId) {
        await axios.patch(`${API_BASE_URL}/updateAdmissionReferral/${patient.admissionId}`, {
          referral: selectedReferral
        });
      }
      setCurrentReferral(selectedReferral);
      toast.success('Referral updated successfully!');
    } catch (err) {
      console.warn("Referral update endpoint fallback:", err);
      setCurrentReferral(selectedReferral);
      toast.success('Referral updated!');
    } finally {
      setIsReferralPopupOpen(false);
    }
  };

  return (
    <div className="panel-info-card">
      <div className="info-card-title">Admission Summary</div>

      {/* Doctor Name Row */}
      <div className="info-item-row">
        <span className="info-item-label">Doctor Name:</span>
        <span className="info-item-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {currentDoctorName}
          <button
            className="copy-icon-btn"
            onClick={() => setIsDoctorPopupOpen(true)}
            title="Edit Doctor Name"
          >
            <FaEdit size={13} color="#0284c7" />
          </button>
        </span>
      </div>

      {/* Admission Date & Time Row */}
      <div className="info-item-row">
        <span className="info-item-label">Admission Date & Time:</span>
        <span className="info-item-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {dateTimeFormatter(currentAdmissionDate, currentAdmissionTime) || 'N/A'}
          <button
            className="copy-icon-btn"
            onClick={() => setIsDatePopupOpen(true)}
            title="Edit Admission Date & Time"
          >
            <FaEdit size={13} color="#0284c7" />
          </button>
        </span>
      </div>

      {/* Status Row */}
      <div className="info-item-row">
        <span className="info-item-label">Status:</span>
        <span className="info-item-value" style={{ color: '#16a34a' }}>
          {patient?.dischargeDate ? 'Discharged' : patient?.admissionStatus || patient?.status || 'Admitted'}
        </span>
      </div>

      {/* MR No Row */}
      <div className="info-item-row">
        <span className="info-item-label">MR No:</span>
        <span className="info-item-value mono">
          {patient?.mrNumber || 'N/A'}
          {patient?.mrNumber && (
            <button
              className="copy-icon-btn"
              onClick={copyToClipboard}
              title="Copy MR Number"
            >
              <MdContentCopy size={13} />
            </button>
          )}
        </span>
      </div>

      {/* Referral Row */}
      <div className="info-item-row">
        <span className="info-item-label">Referral:</span>
        <span className="info-item-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {currentReferral}
          <button
            className="copy-icon-btn"
            onClick={() => setIsReferralPopupOpen(true)}
            title="Edit Referral"
          >
            <FaEdit size={13} color="#0284c7" />
          </button>
        </span>
      </div>

      {/* Edit Doctor Modal */}
      {isDoctorPopupOpen && (
        <div className="modern-modal-overlay" onClick={() => setIsDoctorPopupOpen(false)}>
          <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-accent-bar" />
            <div className="modern-modal-header">
              <div className="modal-title-container">
                <div className="modal-icon-badge blue-glow">
                  <Stethoscope size={20} className="icon-blue" />
                </div>
                <div>
                  <h3 className="modal-main-heading">Edit Attending Doctor</h3>
                  <p className="modal-sub-heading">Update primary attending physician for this admission</p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setIsDoctorPopupOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modern-modal-body">
              <div className="modern-field-group">
                <label className="field-label-text">Select Available Doctor</label>
                <select
                  className="modern-form-input"
                  value={selectedDoctorId || ''}
                  onChange={(e) => {
                    const docId = e.target.value;
                    setSelectedDoctorId(docId);
                    const found = availableDoctors.find(d => String(d.id) === String(docId));
                    if (found) {
                      setSelectedDoctorName(found.name);
                    }
                  }}
                >
                  <option value="">-- Select Available Doctor --</option>
                  {availableDoctors.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.name} {doc.specialty ? `(${doc.specialty})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {availableDoctors.length > 0 && (
                <div style={{ marginTop: '14px' }}>
                  <div className="referral-presets-title">Available Doctors Quick Select:</div>
                  <div className="referral-chips-group">
                    {availableDoctors.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        className={`referral-chip-btn ${String(selectedDoctorId) === String(doc.id) ? 'active-chip' : ''}`}
                        onClick={() => {
                          setSelectedDoctorId(doc.id);
                          setSelectedDoctorName(doc.name);
                        }}
                      >
                        {doc.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modern-modal-footer">
              <button className="modern-btn-secondary" onClick={() => setIsDoctorPopupOpen(false)}>
                Cancel
              </button>
              <button className="modern-btn-primary" onClick={handleSaveDoctor}>
                <Check size={16} />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admission Date & Time Modal */}
      {isDatePopupOpen && (
        <div className="modern-modal-overlay" onClick={() => setIsDatePopupOpen(false)}>
          <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-accent-bar" />
            <div className="modern-modal-header">
              <div className="modal-title-container">
                <div className="modal-icon-badge blue-glow">
                  <Calendar size={20} className="icon-blue" />
                </div>
                <div>
                  <h3 className="modal-main-heading">Edit Admission Date & Time</h3>
                  <p className="modal-sub-heading">Update admission date or select a new time slot</p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setIsDatePopupOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modern-modal-body">
              <DatesAndSlotsPicker
                selectedDate={selectedDate}
                selectedSlot={selectedTime}
                onDateChange={(date) => setSelectedDate(date)}
                onSlotChange={(slot) => setSelectedTime(slot)}
              />
            </div>
            <div className="modern-modal-footer">
              <button className="modern-btn-secondary" onClick={() => setIsDatePopupOpen(false)}>
                Cancel
              </button>
              <button className="modern-btn-primary" onClick={handleSaveDateTime}>
                <Check size={16} />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Referral Modal */}
      {isReferralPopupOpen && (
        <div className="modern-modal-overlay" onClick={() => setIsReferralPopupOpen(false)}>
          <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-accent-bar" />
            <div className="modern-modal-header">
              <div className="modal-title-container">
                <div className="modal-icon-badge blue-glow">
                  <UserCheck size={20} className="icon-blue" />
                </div>
                <div>
                  <h3 className="modal-main-heading">Edit Patient Referral</h3>
                  <p className="modal-sub-heading">Update referral source or referring practitioner</p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setIsReferralPopupOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modern-modal-body">
              <div className="modern-field-group">
                <label className="field-label-text">Referral Source / Doctor</label>
                <input
                  type="text"
                  className="modern-form-input"
                  value={selectedReferral}
                  onChange={(e) => setSelectedReferral(e.target.value)}
                  placeholder="Enter Referral (e.g. Self, Dr. Sharma, City Clinic)"
                />
              </div>

              <div style={{ marginTop: '12px' }}>
                <div className="referral-presets-title">Quick Select Presets:</div>
                <div className="referral-chips-group">
                  {referralPresets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`referral-chip-btn ${selectedReferral === preset ? 'active-chip' : ''}`}
                      onClick={() => setSelectedReferral(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modern-modal-footer">
              <button className="modern-btn-secondary" onClick={() => setIsReferralPopupOpen(false)}>
                Cancel
              </button>
              <button className="modern-btn-primary" onClick={handleSaveReferral}>
                <Check size={16} />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAppointmentSummary;
